import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
	FieldExpression, toString as luceneToString,
	NodeExpression, phrase
} from 'lucene';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf, NgFor, UpperCasePipe } from '@angular/common';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { StepClickedEvent, StepStatus, StepperComponent } from '../../../shared/components/stepper/stepper.component';
import { DEFECT_DETAILED_OPTIONS, UNIT_OPTIONS } from '../../../shared/models/list';
import { User } from '../../../shared/models/user';
import { LocationService } from '../../../shared/services/location.service';
import { TranslationService } from '../../../shared/services/translation.service';
import { UserService } from '../../../shared/services/user.service';
import { BlobService } from '../../../shared/utils/blob.service';
import { OrderBy, OrderByDirection, UNDERSCORE } from '../../../shared/utils/constant';
import { AllQualityIssueAttachment } from '../../models/all-quality-issue-attachment';
import { QualityIssuePendingPushStatus, QualityIssueStatus } from '../../models/all-quality-issue-constants';
import { AllQualityIssue } from '../../models/all-quality-issue';
import { ClaimBatch } from '../../models/claim-batch';
import { AllQualityIssueService } from '../../services/all-quality-issue.service';
import { QualityIssueBatchService } from '../../services/quality-issue-batch-service';
import { SnackbarSaveDraftComponent } from '../utils/snackbar-save-draft.component';
import { SnackbarSubmitClaimComponent } from '../utils/snackbar-submit-claim.component';
import { AllQualityIssueBatch, getQualityIssueBatchQuantity } from '../../models/all-quality-issue-batch';
import { GlobalErrorHandler } from '../../../shared/utils/error-handler';
import { formatNumber } from '../../../shared/utils/number.format';
import { SafeUrlPipe } from '../../../shared/pipes/safeurl.pipe';
import { DimensionPipe } from '../../../shared/pipes/format-dimension.pipe';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';

interface BatchPair {
	claimBatchFromApi: ClaimBatch;
	issueBatch: AllQualityIssueBatch;
}

@Component({
	selector: 'copo-quality-issue-confirmation',
	templateUrl: './quality-issue-confirmation.component.html',
	styleUrl: './quality-issue-confirmation.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		CdkScrollable,
		PageHeaderComponent,

		MatButtonModule,
		TranslateModule,
		MatIconModule,
		StepperComponent,
		NgFor,
		RouterLink,
		LoadingSpinnerComponent,
		UpperCasePipe,
		NumberPipe,
		DimensionPipe,
		SafeUrlPipe
	]
})

export class QualityIssueConfirmationComponent implements OnDestroy {
	submitting = false;

	mainClaimBatch: ClaimBatch | undefined;

	isDetector = false;
	isValidator = false;
	isReader = false;
	hasRole = false;
	selectedBatchList: ClaimBatch[] = [];

	mainClaimBatchPair: BatchPair | undefined;
	selectedBatchPairList: BatchPair[] = [];

	readonly UNDERSCORE = UNDERSCORE;
	claim: AllQualityIssue;

	user: User;

	readonly defectOptions = DEFECT_DETAILED_OPTIONS;
	readonly unitBatchOptions = UNIT_OPTIONS;

	readonly steps;

	private readonly batchDataSub?: Subscription;
	private readonly relatedBatchDataSub?: Subscription;
	private readonly relatedBatchDataSubNew?: Subscription;

	constructor(
		private readonly translationService: TranslationService,
		private readonly locationService: LocationService,
		private readonly userService: UserService,
		private readonly claimService: AllQualityIssueService,
		private readonly batchService: QualityIssueBatchService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly blobService: BlobService,

		private readonly snackBar: MatSnackBar,
		private readonly errorHandler: GlobalErrorHandler
	) {
		this.steps = [
			{
				label: this.translationService.getTR('claim.first.batch.selection'),
				status: StepStatus.ENABLED,
			},
			{
				label: this.translationService.getTR('claim.quality.issue.information'),
				status: StepStatus.ENABLED,
			},
			{
				label: this.translationService.getTR('claim.quality.issue.summary'),
				status: StepStatus.CURRENT,
			}
		];
		const res = <{ claim: AllQualityIssue }> this.route.snapshot.data;
		this.claim = res['claim'];

		this.user = this.userService.user;

		this.isDetector = this.user.isClaimDetector();
		this.isValidator = this.user.isClaimValidator();
		this.isReader = this.user.isClaimReader();
		this.hasRole = this.isDetector || this.isValidator || this.isReader;

		let allNode: NodeExpression | FieldExpression | undefined;

		for (const selectedBatch of this.claim.batches) {
			const ref = selectedBatch.reference;
			const qty = selectedBatch.quantityValue;
			let batchNode: FieldExpression | NodeExpression = {
				field: 'batchReference',
				term: phrase.escape(ref),
				quoted: true
			};
			if (qty) {
				const quantity1Field: FieldExpression = {
					field: 'qtyBatchGross',
					term: String(qty),
					quoted: false
				};
				const quantity2Field: FieldExpression = {
					field: 'qtyBatchGross',
					term: String(qty / 1000),
					quoted: false
				};
				const quantity3Field: FieldExpression = {
					field: 'qtyBatchNet',
					term: String(qty),
					quoted: false
				};
				const quantity4Field: FieldExpression = {
					field: 'qtyBatchNet',
					term: String(qty / 1000),
					quoted: false
				};
				const quantity5Field: FieldExpression = {
					field: 'qtyInvoiced',
					term: String(qty),
					quoted: false
				};
				const quantity6Field: FieldExpression = {
					field: 'qtyInvoiced',
					term: String(qty / 1000),
					quoted: false
				};

				const qty1Node: NodeExpression = {
					left: quantity1Field,
					operator: 'OR',
					parenthesized: false,
					right: quantity2Field
				};

				const qty2Node: NodeExpression = {
					left: qty1Node,
					operator: 'OR',
					parenthesized: false,
					right: quantity3Field
				};

				const qty3Node: NodeExpression = {
					left: qty2Node,
					operator: 'OR',
					parenthesized: false,
					right: quantity4Field
				};

				const qty4Node: NodeExpression = {
					left: qty3Node,
					operator: 'OR',
					parenthesized: false,
					right: quantity5Field
				};

				const qty5Node: NodeExpression = {
					left: qty4Node,
					operator: 'OR',
					parenthesized: true,
					right: quantity6Field
				};

				batchNode = {
					left: batchNode,
					operator: 'AND',
					parenthesized: true,
					right: qty5Node
				};
			}

			allNode = !allNode
				? batchNode
				: {
					left: allNode,
					operator: 'OR',
					parenthesized: false,
					right: batchNode
				};
		}
		if (!allNode) {
			throw new Error('no batch selected in the issue');
		}
		this.relatedBatchDataSubNew = this.batchService.getBatches(
			[
				'id',
				'batchReference',
				'supplierOrderNumber',
				'supplierOrderItemNumber',
				'millOrderNumber',
				'millOrderItemNumber',
				'soldTo',
				'soldToDescription',
				'shipTo',
				'qtyInvoiced',
				'weightUnitInvoiced',
				'currencyInvoiced',
				'sfcC',
				'statusDescription',
				'thicknessDimension',
				'widthDimension',
				'lengthDimension',
				'qtyBatchGross',
				'qtyBatchNet',
				'steelgradeDescription',
				'cddWeek',
				'millDescription',
				'customerOrderNumber',
				'customerOrderItemNumber'
			],
			luceneToString(allNode),
			1,
			50,
			new OrderBy('batchReference', OrderByDirection.ASC)
		)
			.subscribe(relatedBatchList => {
				for (const selectedBatchNew of this.claim.batches.slice(1)) {
					for (const relatedBatch of relatedBatchList.results) {
						if (relatedBatch.batchReference === selectedBatchNew.reference
							&& relatedBatch.qtyInvoiced === selectedBatchNew.quantityValue) {
							this.selectedBatchPairList.push({
								claimBatchFromApi: relatedBatch,
								issueBatch: selectedBatchNew
							});
						}
					}
				}
				for (const selectMainBatch of relatedBatchList.results) {
					if (
						this.claim.batches[0].reference === selectMainBatch.batchReference
						&& this.claim.batches[0].quantityValue === selectMainBatch.qtyInvoiced
					) {
						this.mainClaimBatchPair = {
							claimBatchFromApi: selectMainBatch,
							issueBatch: this.claim.batches[0]
						};
						return;
					}
				}
			});
	}

	lowerCaseUnit(weightUnit: string) {
		return <keyof typeof UNIT_OPTIONS> weightUnit.toLowerCase();
	}

	ngOnDestroy() {
		if (this.batchDataSub) {
			this.batchDataSub.unsubscribe();
		}
		if (this.relatedBatchDataSub) {
			this.relatedBatchDataSub.unsubscribe();
		}
		if (this.relatedBatchDataSubNew) {
			this.relatedBatchDataSubNew.unsubscribe();
		}
	}

	async showAttachment(event: MouseEvent, attachment: AllQualityIssueAttachment) {
		const { href } = <HTMLAnchorElement> event.currentTarget;
		// ie11 blob url opening
		if (!href.startsWith('blob:') || !('msSaveOrOpenBlob' in navigator)) {
			return;
		}
		event.preventDefault();
		const blob = await this.blobService.getBlob(href);
		navigator.msSaveOrOpenBlob(blob, attachment.name);
	}

	quantity(batch: AllQualityIssueBatch) {
		const { value, unit } = getQualityIssueBatchQuantity(batch);
		const unitSymbol = this.unitBatchOptions[unit].symbol;
		if (!value) {
			return '';
		}
		const unitPart = ` ${ unitSymbol }`;
		return `${ formatNumber(
			value,
			'weight'
		) }${ unitPart }`;
	}

	getAttachmentUrl(id: number, attachment: AllQualityIssueAttachment) {
		return attachment.localUrl
			|| this.claimService.getDownloadFile(id, attachment.hash, attachment.name);
	}

	async saveClaim(event: Event, draft: boolean) {
		const { pendingPushStatusTr: previousPendingPushStatusTr, statusTr: previousStatusTr } = this.claim;
		if (!draft) {
			this.claim.pendingPushStatusTr =
				this.claim.statusTr === QualityIssueStatus.DRAFT
					? QualityIssuePendingPushStatus.CREATE : QualityIssuePendingPushStatus.UPDATE;
			this.claim.statusTr = QualityIssueStatus.PENDING_NOT_PUSH;
		}
		event.preventDefault();
		try {
			this.submitting = true;
			try {
				await this.claimService.saveIssue(this.claim);
			} catch (err) {
				console.error(err);
				this.errorHandler.handleError(err);
				this.claim.pendingPushStatusTr = previousPendingPushStatusTr;
				this.claim.statusTr = previousStatusTr;
				this.snackBar.openFromComponent(
					SnackbarSaveDraftComponent,
					{
						duration: 5000,
						panelClass: ['copo-claim-draft-snackbar'],
						data: true
					}
				);
				return;
			}
		} finally {
			this.submitting = false;
		}
		this.claimService.claim = new AllQualityIssue();
		if (this.claim.statusTr === QualityIssueStatus.DRAFT) {
			await this.router.navigate(['/quality-issues/drafts'], { replaceUrl: true });
			this.snackBar.openFromComponent(
				SnackbarSaveDraftComponent,
				{
					duration: 5000,
					panelClass: ['copo-claim-draft-snackbar'],
					data: false
				}
			);
		} else if (this.claim.type !== 'warning') {
			await this.router.navigate(['/quality-issues/list'], { replaceUrl: true });
			this.snackBar.openFromComponent(
				SnackbarSubmitClaimComponent,
				{
					duration: 5000,
					panelClass: ['copo-claim-draft-snackbar'],
					data: this.claim
				}
			);
		} else {
			await this.router.navigate(['/quality-issues/datacenter'], { replaceUrl: true });
			this.snackBar.open(
				this.translationService.getTR('claim.warning.takenintoaccount'),
				undefined,
				{
					duration: 2750
				}
			);
		}
	}

	getAttachments(claim: AllQualityIssue) {
		return claim.attachments;
	}

	async onStepClicked(event: StepClickedEvent) {
		if (event.stepIndex === 0) {
			await this.router.navigate(['../batch-selection'], { relativeTo: this.route });
		}
		if (event.stepIndex === 1) {
			await this.router.navigate(
				['../quality-issue-entry'],
				{ relativeTo: this.route }
			);
		}
	}

	goBack() {
		this.locationService.go(-1, this.claim.steeluserId
			? `../quality-issues/edit/${ this.claim.steeluserId }/quality-issue-entry`
			: '../quality-issues/new/quality-issue-entry');
	}
}
